import { useState } from 'react';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const faqs = [
  {
    id: 'seo-and-geo',
    tag: 'Strategy',
    q: 'Do I still need SEO if I\'m doing GEO?',
    a: 'Yes — and we always recommend running both. SEO and GEO are increasingly separate disciplines. Ranking on Google and being cited by AI require different strategies, different content formats, and different authority signals. The good news is that some foundations overlap — strong website structure, quality content, and genuine authority help both.',
  },
  {
    id: 'measurement',
    tag: 'Results',
    q: 'How do you measure GEO results?',
    a: 'We measure four key metrics: brand mention frequency (how often AI engines reference your business), citation rate (what percentage of mentions include a link), share of voice (your percentage of AI answers compared to competitors), and citation velocity (how quickly new content achieves AI visibility after publishing). You get a monthly report covering all four.',
  },
  {
    id: 'timeline',
    tag: 'Timeline',
    q: 'How long until I see results?',
    a: 'Most clients see their first consistent AI citations within 6–10 weeks of starting the content and authority programme. Significant share of voice improvements typically appear within 3–4 months. Unlike paid ads, GEO results compound over time — the longer you invest, the stronger your position becomes.',
  },
  {
    id: 'platforms',
    tag: 'Platforms',
    q: 'Which AI platforms do you optimise for?',
    a: 'We optimise across ChatGPT (OpenAI), Google Gemini and AI Overviews, Perplexity AI, Microsoft Copilot (Bing AI), and Claude (Anthropic). As new AI search platforms emerge, we add them to the programme.',
  },
  {
    id: 'guarantee',
    tag: 'Expectations',
    q: 'Can you guarantee I\'ll appear in AI answers?',
    a: 'No agency can guarantee specific AI citations — AI systems update constantly and make their own decisions about what to cite. What we can guarantee is that we\'ll implement every known best practice to maximise your citation rate, track your progress transparently, and adapt the strategy as platforms evolve.',
  },
  {
    id: 'no-content',
    tag: 'Starting Point',
    q: 'What if I don\'t have much content on my website?',
    a: 'That\'s actually a great starting position — it means there\'s significant opportunity. We\'ll create the foundational content your business needs, structured from day one for AI retrieval. You\'ll be building authority correctly from the ground up rather than having to undo years of poorly structured content.',
  },
  {
    id: 'google-too',
    tag: 'Google',
    q: 'Is this just for new AI tools or does it affect Google too?',
    a: 'Both. Google\'s AI Overviews now appear in up to 60% of searches — meaning GEO work directly improves your visibility in Google\'s own AI-generated answers, not just third-party platforms like ChatGPT. A strong GEO strategy effectively future-proofs your entire search presence.',
  },
  {
    id: 'existing-seo',
    tag: 'Existing SEO',
    q: 'I already rank well on Google. Do I still need GEO?',
    a: 'Yes — and this is the most common misconception we hear. The overlap between Google top results and AI-cited sources has dropped below 20%. Ranking on page one of Google does not mean AI engines recommend you. They are entirely different signals, built through entirely different strategies. GEO is not a replacement for your SEO investment — it\'s the next layer on top.',
  },
];

export default function GeoFaqSection() {
  const [openId, setOpenId] = useState<string>('seo-and-geo');
  const groupRef = useScrollRevealGroup();

  return (
    <section id="geo-faq" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-border to-transparent" />
      <div className="absolute inset-0 hero-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-ds-red/4 blur-[150px] rounded-full pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6" ref={groupRef}>

        {/* Header */}
        <div className="mb-16">
          <span className="section-label reveal">Frequently Asked Questions</span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none reveal">
              COMMON QUESTIONS
              <br />
              ABOUT
              <br />
              <span className="text-ds-red neon-text-red">GEO / AEO.</span>
            </h2>
            <p className="font-body text-white/50 text-sm leading-relaxed max-w-xs reveal">
              Straight answers to what we get asked most. If yours isn&apos;t here, the contact form is right below — we answer every enquiry personally, same day.
            </p>
          </div>
          <div className="mt-8 h-px w-24 bg-ds-red reveal" style={{ boxShadow: '0 0 12px rgba(180,255,57,0.5)' }} />
        </div>

        {/* Two-column accordion */}
        <div className="grid lg:grid-cols-2 gap-px bg-ds-border/30 reveal">
          {[faqs.slice(0, 4), faqs.slice(4)].map((col, ci) => (
            <div key={ci} className="bg-ds-bg space-y-px">
              {col.map((faq) => {
                const isOpen = openId === faq.id;
                return (
                  <div
                    key={faq.id}
                    className={`border transition-colors duration-300 ${isOpen ? 'border-ds-red/40 bg-ds-bg-2' : 'border-ds-border bg-ds-bg hover:border-white/15'}`}
                    style={isOpen ? { boxShadow: '0 0 20px rgba(180,255,57,0.06)' } : {}}
                  >
                    <button
                      onClick={() => setOpenId(isOpen ? '' : faq.id)}
                      className="w-full flex items-start gap-4 p-5 text-left cursor-pointer"
                    >
                      <div className="flex-1">
                        <span className={`inline-block border px-2 py-0.5 font-body text-xs tracking-widest uppercase mb-2 transition-colors duration-300 ${isOpen ? 'border-ds-red/40 text-ds-red bg-ds-red/8' : 'border-ds-border text-white/30'}`}>
                          {faq.tag}
                        </span>
                        <p className={`font-display text-sm tracking-wide leading-snug transition-colors duration-300 ${isOpen ? 'text-white' : 'text-white/60'}`}>
                          {faq.q}
                        </p>
                      </div>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1 border transition-all duration-300 ${isOpen ? 'border-ds-red/50 rotate-45' : 'border-ds-border'}`}>
                        <i className="ri-add-line text-white/60 text-sm" />
                      </div>
                    </button>
                    <div
                      className="overflow-hidden transition-all duration-300"
                      style={{ maxHeight: isOpen ? '400px' : '0' }}
                    >
                      <p className="font-body text-white/55 text-sm leading-relaxed px-5 pb-5">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div
          className="mt-16 border border-ds-border bg-ds-bg p-10 flex flex-col md:flex-row items-start justify-between gap-8 reveal"
          style={{ borderLeft: '3px solid #B4FF39' }}
        >
          <div>
            <h3 className="font-display text-2xl text-white mb-1 tracking-wide">STILL GOT A QUESTION?</h3>
            <p className="font-body text-white/45 text-sm leading-relaxed">
              Ask us directly — we answer every enquiry personally, same day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-shrink-0">
            <a
              href="mailto:hello@darksidedigital.com.au"
              className="btn-outline text-xs flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-mail-line" />
              Email Us
            </a>
            <button
              onClick={() => {
                const el = document.getElementById('geo-pricing');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-red text-xs whitespace-nowrap"
            >
              Free AI Visibility Audit
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}
